import { useState, useEffect } from "react";
import { useModalStore } from "../../../stores/modalStore.js";
import { useChannelStore } from "../../../stores/channelStore.js";
import { useServerStore } from "../../../stores/serverStore.js";
import { useDirectMessageStore } from "../../../stores/directMessageStore.js";

export function QuickSwitcherModal() {
  const { isOpen, type, onClose } = useModalStore();
  const channels = useChannelStore((state) => state.channels);
  const servers = useServerStore((state) => state.servers);
  const conversations = useDirectMessageStore((state) => state.conversations);
  const setCurrentChannel = useChannelStore((state) => state.setCurrentChannel);
  const setCurrentServer = useServerStore((state) => state.setCurrentServer);
  const setCurrentConversation = useDirectMessageStore((state) => state.setCurrentConversation);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(0);

  const isModalOpen = isOpen && type === "quickSwitcher";

  useEffect(() => {
    if (!isModalOpen) {
      setSearch("");
      setSelected(0);
    }
  }, [isModalOpen]);

  if (!isModalOpen) return null;

  const allItems = [
    ...channels.map((ch) => ({ type: "channel", id: ch.id, name: `#${ch.name}`, category: ch.category })),
    ...servers.map((srv) => ({ type: "server", id: srv.id, name: srv.name, badge: srv.id })),
    ...conversations.map((conv) => ({ type: "dm", id: conv.id, name: conv.userName, status: "online" })),
  ];

  const filtered = allItems.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (item) => {
    if (item.type === "channel") {
      setCurrentChannel(item.id);
    } else if (item.type === "server") {
      setCurrentServer(item.id);
    } else if (item.type === "dm") {
      setCurrentConversation(item.id);
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && filtered[selected]) {
      e.preventDefault();
      handleSelect(filtered[selected]);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 1000,
        paddingTop: "15vh",
      }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgb(var(--aethex-panel))",
          border: "1px solid rgb(var(--aethex-border))",
          borderRadius: "8px",
          width: "90%",
          maxWidth: "600px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}
      >
        <div style={{ padding: "16px" }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Quick jump to channel or DM..."
            autoFocus
            style={{
              width: "100%",
              background: "rgb(var(--aethex-bg))",
              border: "1px solid rgb(var(--aethex-border))",
              borderRadius: "4px",
              padding: "12px 16px",
              color: "rgb(var(--aethex-text))",
              fontFamily: "'Roboto Mono', monospace",
              fontSize: "1rem",
              outline: "none",
            }}
          />
        </div>

        <div style={{ maxHeight: "400px", overflow: "auto" }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "40px 16px", textAlign: "center", color: "rgb(var(--aethex-dim))" }}>
              <p>No results found</p>
            </div>
          ) : (
            filtered.map((item, idx) => (
              <div
                key={`${item.type}-${item.id}`}
                onClick={() => handleSelect(item)}
                style={{
                  padding: "12px 16px",
                  background: selected === idx ? "rgb(var(--aethex-surface))" : "transparent",
                  cursor: "pointer",
                  transition: "background 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
                onMouseEnter={() => setSelected(idx)}
              >
                <span style={{ fontSize: "1.25rem", color: "rgb(var(--aethex-primary))" }}>
                  {item.type === "channel" ? "#" : item.type === "server" ? "🖥️" : "👤"}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "rgb(var(--aethex-text))", fontWeight: "500" }}>{item.name}</div>
                  {item.category && (
                    <div style={{ fontSize: "0.75rem", color: "rgb(var(--aethex-dim))" }}>{item.category}</div>
                  )}
                </div>
                {item.type === "server" && (
                  <span style={{ fontSize: "0.75rem", color: "rgb(var(--aethex-dim))" }}>{item.badge}</span>
                )}
              </div>
            ))
          )}
        </div>

        <div style={{ padding: "12px 16px", borderTop: "1px solid rgb(var(--aethex-border))", fontSize: "0.75rem", color: "rgb(var(--aethex-dim))" }}>
          <span>↑↓ Navigate</span> <span style={{ marginLeft: "16px" }}>↵ Select</span> <span style={{ marginLeft: "16px" }}>Esc Close</span>
        </div>
      </div>
    </div>
  );
}
